import {Badge, Breadcrumb, Button, Col, Form} from "react-bootstrap";
import DataTable from "react-data-table-component";
import {BsGlobe, BsPencilSquare} from "react-icons/bs";
import {MdDeleteOutline} from "react-icons/md";
import {Link, useNavigate} from "react-router-dom";
import {FloatingButton} from "../../components";
import CircleLoader from "../../components/Loaders/CircleLoader";
import Layout from "../../components/layouts/layout";
import {Permission} from "../../configs/constants";
import ComponentGuard from "../../helpers/ComponentGuard";
import {useEventList, useLang, useTermList} from "../../hooks";
import {useNotification} from "../../hooks/useNotification";
import {IoExtensionPuzzleOutline} from "react-icons/io5";
import moment from "moment";
import {Controller, useForm} from "react-hook-form";
import {DatePicker} from "rsuite";

export function EventListPage() {
  const {t} = useLang();
  const {notifyError, notifySuccess} = useNotification();
  const {register, handleSubmit, control} = useForm();

  const {
    list,
    pagination,
    changePagination,
    error,
    isLoading,
    deleteItem,
    filterDispatch
  } = useEventList();

  const {
    termList,
    error: termsError,
    isLoading: isTermsLoading,
  } = useTermList(11, {limit: 1000, page: 1});


  const navigate = useNavigate();
  if (error) {
    notifyError(error);
    setTimeout(() => navigate("/"), 200);
  }

  const handleDelete = async (id) => {
    try {
      await deleteItem(id);
      notifySuccess({
        title: "Post removed successfully"
      });
    } catch (err) {
      notifyError({
        title: "Failed to remove content!",
        message: err.message
      });
    }
  };

  const columns = [
    {
      name: "#",
      cell: (row, index) =>
        (pagination.page - 1) * pagination.limit + (index + 1), //RDT provides index by default
      width: "50px"
    },
    {
      name: t("events_manage_col_title"),
      selector: (row) => (
        <div className="text-truncate" style={{width: "200px"}}>
          {row.title}
        </div>
      ),
      sortable: true
    },
    {
      name: t("events_manage_col_eventState"),
      selector: (row) => {
        let bg = "primary";
        switch (row.type) {
          case "Upcoming":
            bg = "success";
            break;
          case "Ongoing"  :
            bg = "primary";
            break;
          default:
            bg = "secondary";
            break;
        }
        return <Badge bg={bg}>{row.type}</Badge>;
      },
      sortable: true
    },
    {
      name: t("events_manage_col_category"),
      selector: (row) => row.event_category?.name ?? "Not Available",
      sortable: true
    },
    {
      name: t("events_manage_col_Duration"),
      selector: (row) => {
        return moment.duration(moment(row.start_date).diff(moment(row.end_date))).humanize();
      },
      sortable: true
    },
    {
      name: t("events_manage_col_time"),
      width: "300px",
      selector: (row) =>
        `${moment(row.start_date).format("DD-MM-YY (hh:mm a)")} - ${moment(row.end_date).format("DD-MM-YY (hh:mm a)")}`,
      sortable: true
    },
    {
      name: t("events_manage_col_status"),
      selector: (row) => <Badge
        bg={row.status ? "success" : "danger"}>{row.status ? "Active" : "Deactive"}</Badge>,
      sortable: true
    },
    {
      name: t("events_manage_col_action"),
      width: "300px",
      selector: (row) => (
        <div className="d-flex gap-2">
          <ComponentGuard needsPermission={[Permission.EventUpdate]}>
            <Link to={`/events/${row.id}/edit`} className="btn btn-secondary">
              <BsPencilSquare />
            </Link>
          </ComponentGuard>
          <ComponentGuard needsPermission={[Permission.EventDelete]}>
            <Button variant="danger" onClick={() => handleDelete(row.id)}>
              <MdDeleteOutline />
            </Button>
          </ComponentGuard>
          <ComponentGuard needsPermission={[Permission.EventLanguagesList]}>
            <Link to={`/langs/event/${row.id}`} className="btn btn-info">
              <BsGlobe />
            </Link>
          </ComponentGuard>
        </div>
      )
    }
  ];

  return (
    <>
      <Layout>
        {isLoading && <CircleLoader />}
        <header>
          <div className="d-flex justify-content-between">
            <div>
              <Breadcrumb>
                <Breadcrumb.Item active>
                  {t("events_manage_events")}
                </Breadcrumb.Item>
              </Breadcrumb>
            </div>
          </div>
        </header>
        <section id="post-list" className="mb-5 pb-5">
          <h2>{t("events_manage_eventsList")}</h2>
          <div className="bg-light rounded p-4 mt-4">
            <Form
              onSubmit={handleSubmit((data) => {
                if (data.post_date) {
                  data.post_date = moment(data.post_date).format("yyyy-MM-DD");
                }
                filterDispatch({
                  type: "reInitialize",
                  payload: data
                });
              })}
            >
              <div className="d-flex flex-column align-items-stretch align-items-md-end flex-md-row gap-2">
                <Form.Group controlId="category">
                  <Form.Label>{t("events_input_category")}</Form.Label>
                  <Form.Control {...register("category")} as="select" custom>
                    <option value="">None</option>
                    {termList.terms.map((trm) => (
                      <option value={trm.id}>{trm.name}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
                <Form.Group controlId="visible">
                  <Form.Label>{t("events_input_status")}</Form.Label>
                  <Form.Control {...register("status")} as="select" custom>
                    <option value={""}>None</option>
                    <option value={1}>Active</option>
                    <option value={0}>Inactive</option>
                  </Form.Control>
                </Form.Group>

                <Form.Group md={2} controlId="type">
                  <Form.Label className="mark-required">{t("events_input_eventType")}</Form.Label>
                  <Form.Control {...register("type")} as="select" custom>
                    <option value={""}>None</option>
                    <option value="Ongoing">{t("events_input_eventType_ongoing")}</option>
                    <option value="Upcoming">{t("events_input_eventType_upcoming")}</option>
                    <option value="Concluded">{t("events_input_eventType_concluded")}</option>
                  </Form.Control>
                </Form.Group>
                <Form.Group
                  controlId="search"
                  onSubmit={handleSubmit((data) => {
                  })}
                >
                  <Form.Label>{t("posts_manage_search")}</Form.Label>
                  <Form.Control
                    {...register("term")}
                    placeholder={t("post_manage_search_plhd")}
                    custom
                  ></Form.Control>
                </Form.Group>
                <div className="ml-auto">
                  <Button className="px-5" type="submit">
                    {t("posts_manage_go")}
                  </Button>
                </div>
              </div>
            </Form>
          </div>
          <DataTable
            columns={columns}
            data={list.events}
            progressPending={isLoading}
            pagination
            paginationServer
            paginationComponentOptions={{noRowsPerPage: true}}
            paginationTotalRows={list.total}
            onChangePage={(page) => {
              changePagination({page});
            }}
            highlightOnHover
            subHeader
            subHeaderAlign="right"
          />
        </section>
      </Layout>
      <ComponentGuard needsPermission={[Permission.EventStore]}>
        <FloatingButton
          onClick={() => {
            navigate("/events/new");
          }}
          icon={<IoExtensionPuzzleOutline />}
          name={t("events_create_createNewEvent")}
        />
      </ComponentGuard>
    </>
  );
}

export default EventListPage;
